/**
 * Create User Modal — extracted from AdminDashboard.jsx.
 *
 * Presentational. Parent owns `newUser` state, `open`, and `onSubmit`.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

export function CreateUserModal({ open, onOpenChange, newUser, setNewUser, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col h-[90vh] sm:h-auto sm:max-h-[90vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>Manually create a new user account</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              placeholder="First name only — no last names please (nicknames welcome)"
            />
            <p className="text-xs text-muted-foreground">First name only — no last names please. Nicknames welcome.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="email@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              placeholder="••••••••"
            />
          </div>
          <div className="space-y-2">
            <Label>Subscription Status</Label>
            <Select
              value={newUser.subscription_status}
              onValueChange={(value) => setNewUser({ ...newUser, subscription_status: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="active">Active</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_admin"
              checked={newUser.is_admin}
              onCheckedChange={(checked) => setNewUser({ ...newUser, is_admin: checked })}
              data-testid="create-user-admin-checkbox"
            />
            <Label htmlFor="is_admin">Admin privileges</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_gifted"
              checked={newUser.is_gifted}
              onCheckedChange={(checked) => setNewUser({ ...newUser, is_gifted: checked })}
              data-testid="create-user-gifted-checkbox"
            />
            <Label htmlFor="is_gifted">Gifted (free VIP access)</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_contributor"
              checked={newUser.is_contributor}
              onCheckedChange={(checked) => setNewUser({ ...newUser, is_contributor: checked })}
              data-testid="create-user-contributor-checkbox"
            />
            <Label htmlFor="is_contributor">Contributor (blog access)</Label>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="create-user-cancel">
            Cancel
          </Button>
          <Button onClick={onSubmit} data-testid="create-user-submit">Create User</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateUserModal;
